import { Form, Row, Col, Input, InputNumber, Button, Checkbox, Select } from "antd";
import { mobileFormValidator, parseStringOrList } from "common/utils";
import { AppActionStrip } from "components/AppActionStrip/AppActionStrip";
import { FormGroupIdentifier } from "components/FormGroupIdentifier/FormGroupIdentifier";
import CustomUploadField from "components/FormUploadField/FormUploadField";
import { PurchaseCodesField } from "components/PurchaseCodesField/PurchaseCodesField";
import { useEffect, useState } from "react";
import { getActivePurchaseCodes, getSupplyLocations } from "store/vendor/vendorSlice";
import { getVendorB2cAll } from "store/vendor-items/vendorItemsSlice";
import { useAppDispatch } from "hooks/useAppDispatch";
import { useAppSelector } from "hooks/useAppSelector";

interface VendorFormProps {
  formInstance: any;
  onFinish: (values: Record<string, any>) => void;
  onCancel: () => void;
  onSubmit: () => void;
  values?: any;
  loading?: boolean;
  /** ID of the vendor being edited — excluded from parent vendor picker */
  editingVendorId?: string;
}

const { Option } = Select;

const toArray = (val: any): any[] => (Array.isArray(val) ? val : []);

/** Same city options as the WMS Flutter app vendor screen */
export const VENDOR_CITY_OPTIONS = ["Gurugram", "Noida", "Bangalore", "Delhi"];

export const VendorForm = (props: VendorFormProps) => {
  const dispatch = useAppDispatch();
  const { activePurchaseCodes } = useAppSelector((state) => state.vendors);
  const initConfig = useAppSelector((state) => state.app.initConfig);
  const b2cVendors = useAppSelector((state) => state.vendorItems.vendors);

  const safeServices = parseStringOrList(initConfig?.vendorServices);
  const safeTypes = parseStringOrList(initConfig?.vendorTypes);
  const safePaymentMethods = parseStringOrList(initConfig?.vendorPaymentMethods);
  const safePurchaseCodes = toArray(activePurchaseCodes);

  // All vendors excluding self (for parent vendor picker) — full b2c list like Flutter
  const vendorOptions = toArray(b2cVendors?.data).filter(
    (v: any) => props.editingVendorId === undefined || v.id?.toString() !== props.editingVendorId
  );

  const [supplyLocations, setSupplyLocations] = useState<string[]>([]);
  const [sameAsPhone, setSameAsPhone] = useState(false);
  const [vendorType, setVendorType] = useState<string | undefined>(props.values?.vendorType);

  // Markets come from init config (vendorMarkets) like the Flutter app;
  // fall back to the supply-locations API when the config has none.
  const configMarkets = parseStringOrList(initConfig?.vendorMarkets);
  const marketOptions = configMarkets.length ? configMarkets : supplyLocations;

  useEffect(() => {
    dispatch(getSupplyLocations()).then((action: any) => {
      if (action.type === getSupplyLocations.fulfilled.type) {
        setSupplyLocations(Array.isArray(action.payload) ? action.payload : []);
      }
    });
    dispatch(getActivePurchaseCodes());
    dispatch(getVendorB2cAll({ pageNo: 1, pageSize: 1000 }));
  }, [dispatch]);

  useEffect(() => {
    props?.formInstance.setFieldsValue(props.values);
    if (props.values?.vendorType) setVendorType(props.values.vendorType);
  }, [props.formInstance, props.values]);

  const onPrimaryPhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (sameAsPhone) {
      props.formInstance.setFieldsValue({ whatsappPhone: e.target.value });
    }
  };

  const onSameAsPhoneChange = (checked: boolean) => {
    setSameAsPhone(checked);
    if (checked) {
      const primary = props.formInstance.getFieldValue("primaryPhone");
      props.formInstance.setFieldsValue({ whatsappPhone: primary });
    }
  };

  return (
    <Form
      form={props.formInstance}
      name="vendor-form"
      layout="vertical"
      style={{ width: "100%" }}
      onFinish={props.onFinish}
    >
      {/* ── Section 1: Basic Information ── */}
      <Row>
        <Col xs={24}>
          <FormGroupIdentifier>Basic Information</FormGroupIdentifier>
        </Col>
        <Col xs={24}>
          <Row gutter={24}>
            <Col xs={24} md={8}>
              <Form.Item
                name="name"
                label="Vendor Name"
                rules={[{ required: true, message: "Please input Vendor Name!" }]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item
                name="pocName"
                label="Point of Contact Name"
                rules={[{ required: true, message: "Please input POC Name!" }]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item
                name="primaryPhone"
                label="Primary Phone"
                rules={[
                  { required: true, message: "Please input Primary Phone!" },
                  { pattern: /^[0-9]+$/, message: "Phone must contain digits only!" },
                  {
                    validator: (_, value) => mobileFormValidator(value),
                    validateTrigger: "onFinish",
                  },
                ]}
              >
                <Input maxLength={10} onChange={onPrimaryPhoneChange} placeholder="10-digit mobile number" />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item
                name="whatsappPhone"
                label={
                  <span>
                    WhatsApp Number&nbsp;
                    <Checkbox
                      checked={sameAsPhone}
                      onChange={(e) => onSameAsPhoneChange(e.target.checked)}
                      style={{ fontSize: 12, fontWeight: "normal" }}
                    >
                      Same as primary
                    </Checkbox>
                  </span>
                }
                rules={[
                  { required: true, message: "Please input WhatsApp Number!" },
                  { pattern: /^[0-9]+$/, message: "Phone must contain digits only!" },
                  {
                    validator: (_, value) => mobileFormValidator(value),
                    validateTrigger: "onFinish",
                  },
                ]}
              >
                <Input maxLength={10} disabled={sameAsPhone} placeholder="10-digit mobile number" />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item
                name="panNumber"
                label="PAN Number"
                rules={[
                  { required: true, message: "Please input PAN Number!" },
                  {
                    pattern: /^[A-Za-z]{5}\d{4}[A-Za-z]$/,
                    message: "Invalid PAN format (e.g. ABCDE1234F)",
                  },
                ]}
              >
                <Input style={{ textTransform: "uppercase" }} placeholder="e.g. ABCDE1234F" />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item
                name="vendorEmail"
                label="Vendor Email"
                rules={[{ type: "email", message: "Please enter a valid email!" }]}
              >
                <Input type="email" placeholder="vendor@example.com" />
              </Form.Item>
            </Col>
          </Row>
        </Col>
      </Row>

      {/* ── Section 2: Business Details ── */}
      <Row>
        <Col xs={24}>
          <FormGroupIdentifier>Business Details</FormGroupIdentifier>
        </Col>
        <Col xs={24}>
          <Row gutter={24}>
            <Col xs={24} md={8}>
              <Form.Item
                name="vendorService"
                label="Vendor Service"
                rules={[{ required: true, message: "Please select Vendor Service!" }]}
              >
                <Select placeholder="Select service">
                  {safeServices.map((s) => (
                    <Option key={s} value={s}>{s}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item
                name="vendorType"
                label="Vendor Type"
                rules={[{ required: true, message: "Please select Vendor Type!" }]}
              >
                <Select
                  placeholder="Select type"
                  onChange={(val) => {
                    setVendorType(val);
                    if (val !== "CHILD") {
                      props.formInstance.setFieldsValue({ parentVendorId: undefined });
                    }
                  }}
                >
                  {safeTypes.map((t) => (
                    <Option key={t} value={t}>{t}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            {vendorType === "CHILD" && (
              <Col xs={24} md={8}>
                <Form.Item
                  name="parentVendorId"
                  label="Parent Vendor"
                  rules={[{ required: true, message: "Please select a Parent Vendor!" }]}
                >
                  <Select
                    showSearch
                    placeholder="Search and select parent vendor"
                    optionFilterProp="label"
                    options={vendorOptions.map((v: any) => ({
                      value: v.id?.toString(),
                      label: `${v.name}${v.vendorCode ? ` (${v.vendorCode})` : ""}`,
                    }))}
                  />
                </Form.Item>
              </Col>
            )}
            <Col xs={24} md={8}>
              <Form.Item name="market" label="Market">
                <Select placeholder="Select market" allowClear>
                  {marketOptions.map((loc) => (
                    <Option key={loc} value={loc}>{loc}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>
        </Col>

        {/* Address sub-section */}
        <Col xs={24} style={{ marginTop: 8 }}>
          <Row gutter={24}>
            <Col xs={24} md={12}>
              <Form.Item
                name="address1"
                label="Address Line 1"
                rules={[{ required: true, message: "Please input Address Line 1!" }]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="address2" label="Address Line 2">
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} md={6}>
              <Form.Item
                name="city"
                label="City"
                rules={[{ required: true, message: "Please select City!" }]}
              >
                <Select placeholder="Select city">
                  {VENDOR_CITY_OPTIONS.map((c) => (
                    <Option key={c} value={c}>{c}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} md={6}>
              <Form.Item
                name="state"
                label="State"
                rules={[{ required: true, message: "Please input State!" }]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} md={6}>
              <Form.Item
                name="pincode"
                label="Pincode"
                rules={[{ required: true, message: "Please input Pincode!" }]}
              >
                <Input type="number" />
              </Form.Item>
            </Col>
          </Row>
        </Col>
      </Row>

      {/* ── Section 3: Bank Details ── */}
      <Row>
        <Col xs={24}>
          <FormGroupIdentifier>Bank Details</FormGroupIdentifier>
        </Col>
        <Col xs={24}>
          <Row gutter={24}>
            <Col xs={24} md={8}>
              <Form.Item
                name="accountNumber"
                label="Account Number"
                rules={[{ required: true, message: "Please input Account Number!" }]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item
                name="accountName"
                label="Account Holder Name"
                rules={[{ required: true, message: "Please input Account Holder Name!" }]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item
                name="ifscCode"
                label="IFSC Code"
                rules={[{ required: true, message: "Please input IFSC Code!" }]}
              >
                <Input style={{ textTransform: "uppercase" }} placeholder="e.g. SBIN0001234" />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item
                name="bankName"
                label="Bank Name"
                rules={[{ required: true, message: "Please input Bank Name!" }]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item
                name="branchName"
                label="Branch Name"
                rules={[{ required: true, message: "Please input Branch Name!" }]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name="paymentMethod" label="Payment Method">
                <Select placeholder="Select payment method" allowClear>
                  {safePaymentMethods.map((m) => (
                    <Option key={m} value={m}>{m}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name="gstin" label="GST Number">
                <Input style={{ textTransform: "uppercase" }} placeholder="e.g. 22AAAAA0000A1Z5" />
              </Form.Item>
            </Col>
          </Row>
        </Col>
      </Row>

      {/* ── Section 4: Map Purchase SKU ── */}
      <Row>
        <Col xs={24}>
          <FormGroupIdentifier>Map Purchase SKU</FormGroupIdentifier>
        </Col>
        <Col xs={24}>
          <Row gutter={24}>
            <Col xs={24} md={16}>
              <Form.Item
                name="purchaseCodes"
                label="Mapped Purchase SKUs"
                rules={[
                  {
                    validator: (_, value) =>
                      Array.isArray(value) && value.length
                        ? Promise.resolve()
                        : Promise.reject(new Error("Please select at least one Purchase SKU!")),
                  },
                ]}
              >
                <PurchaseCodesField options={safePurchaseCodes} />
              </Form.Item>
            </Col>
          </Row>
        </Col>
      </Row>

      {/* ── Section 5: Documents & Location ── */}
      <Row>
        <Col xs={24}>
          <FormGroupIdentifier>Documents &amp; Location</FormGroupIdentifier>
        </Col>
        <Col xs={24}>
          <Row gutter={24}>
            <Col xs={24} md={8}>
              <Form.Item name="shopImageUrl" label="Shop Image">
                <CustomUploadField form={props.formInstance} fieldName="shopImageUrl" />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item
                name="adhaarImageUrl"
                label="Aadhaar Image"
                rules={[{ required: true, message: "Aadhaar Image is required!" }]}
              >
                <CustomUploadField form={props.formInstance} fieldName="adhaarImageUrl" />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item
                name="panImageUrl"
                label="PAN Image"
                rules={[{ required: true, message: "PAN Image is required!" }]}
              >
                <CustomUploadField form={props.formInstance} fieldName="panImageUrl" />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name="licenseFileUrl" label="License Document">
                <CustomUploadField form={props.formInstance} fieldName="licenseFileUrl" />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name="gstCertificateFileUrl" label="GST Certificate">
                <CustomUploadField form={props.formInstance} fieldName="gstCertificateFileUrl" />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item
                name="cancelledChequeFileUrl"
                label="Cancelled Cheque"
                rules={[{ required: true, message: "Cancelled Cheque is required!" }]}
              >
                <CustomUploadField form={props.formInstance} fieldName="cancelledChequeFileUrl" />
              </Form.Item>
            </Col>

            {/* Lat/Lng — manual input replacing mobile GPS auto-capture */}
            <Col xs={24} md={6}>
              <Form.Item
                name="latitude"
                label="Latitude"
                rules={[{ type: "number", min: -90, max: 90, message: "Must be -90 to 90" }]}
              >
                <InputNumber style={{ width: "100%" }} placeholder="e.g. 28.6139" step={0.000001} />
              </Form.Item>
            </Col>
            <Col xs={24} md={6}>
              <Form.Item
                name="longitude"
                label="Longitude"
                rules={[{ type: "number", min: -180, max: 180, message: "Must be -180 to 180" }]}
              >
                <InputNumber style={{ width: "100%" }} placeholder="e.g. 77.2090" step={0.000001} />
              </Form.Item>
            </Col>
          </Row>
        </Col>
      </Row>

      <Row>
        <AppActionStrip>
          <Button type="text" className="action-strip__item" disabled={props.loading} onClick={props.onCancel}>
            Cancel
          </Button>
          <Button type="primary" className="action-strip__item" onClick={props.onSubmit} loading={props.loading} disabled={props.loading}>
            Save
          </Button>
        </AppActionStrip>
      </Row>
    </Form>
  );
};
